import React, { useRef } from 'react';
import { GRID_SIZE } from './constants';

function snapToGrid(v) {
  return Math.round(v / GRID_SIZE) * GRID_SIZE;
}

/**
 * A draggable vertex handle on the polygon in SVG space.
 * Double-click removes the point (if polygon has > 3 points).
 */
function PolygonHandle({ x, y, index, zoom, polygon, onPolygonChange }) {
  const startRef = useRef(null);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    startRef.current = { sx: e.clientX, sy: e.clientY, px: x, py: y };

    const onMove = (me) => {
      if (!startRef.current) return;
      const { sx, sy, px, py } = startRef.current;
      const nx = snapToGrid(px + (me.clientX - sx) / zoom);
      const ny = snapToGrid(py + (me.clientY - sy) / zoom);
      onPolygonChange(polygon.map((pt, i) => (i === index ? { x: nx, y: ny } : pt)));
    };
    const onUp = () => {
      startRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <g>
      <circle
        cx={x}
        cy={y}
        r={9}
        fill="white"
        stroke="#2a5d32"
        strokeWidth={2.5}
        style={{ cursor: 'move', pointerEvents: 'all' }}
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => {
          e.stopPropagation();
          if (polygon.length > 3) {
            onPolygonChange(polygon.filter((_, i) => i !== index));
          }
        }}
      />
      <text
        x={x}
        y={y + 4}
        textAnchor="middle"
        fontSize={9}
        fontWeight={700}
        fill="#2a5d32"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {index + 1}
      </text>
    </g>
  );
}

/**
 * SVG overlay rendering the venue boundary and grid.
 *
 * Props:
 *   venueWidth, venueHeight  – canvas dimensions in pixels
 *   venuePolygon             – [{x,y}, ...] | null  (null = rectangle)
 *   editingPolygon           – boolean – enables click-to-add + vertex handles
 *   zoom                     – current canvas zoom (for coordinate conversion)
 *   onPolygonChange          – (newPolygon) => void
 */
export default function VenueBackground({
  venueWidth,
  venueHeight,
  venuePolygon,
  editingPolygon,
  zoom = 1,
  onPolygonChange,
}) {
  const gridId = `seating-grid-${venueWidth}-${venueHeight}`;
  const clipId = `venue-clip-${venueWidth}-${venueHeight}`;

  const polygonStr =
    venuePolygon?.length >= 3
      ? venuePolygon.map((p) => `${p.x},${p.y}`).join(' ')
      : null;

  const handleBgClick = (e) => {
    if (!editingPolygon || !onPolygonChange) return;
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    // rect.width = venueWidth * zoom  →  svgX = (clientX - rect.left) / zoom
    const nx = snapToGrid((e.clientX - rect.left) / zoom);
    const ny = snapToGrid((e.clientY - rect.top) / zoom);
    onPolygonChange([...(venuePolygon ?? []), { x: nx, y: ny }]);
  };

  return (
    <svg
      width={venueWidth}
      height={venueHeight}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: editingPolygon ? 'all' : 'none',
        zIndex: editingPolygon ? 50 : 0,
        cursor: editingPolygon ? 'crosshair' : 'default',
      }}
      onClick={handleBgClick}
    >
      <defs>
        <pattern
          id={gridId}
          width={GRID_SIZE}
          height={GRID_SIZE}
          patternUnits="userSpaceOnUse"
        >
          <path
            d={`M ${GRID_SIZE} 0 L 0 0 0 ${GRID_SIZE}`}
            fill="none"
            stroke="rgba(0,0,0,0.07)"
            strokeWidth="0.5"
          />
        </pattern>
        {polygonStr && (
          <clipPath id={clipId}>
            <polygon points={polygonStr} />
          </clipPath>
        )}
      </defs>

      {/* Outer background – shows as "outside venue" area when polygon is used */}
      <rect width={venueWidth} height={venueHeight} fill={polygonStr ? '#ddd9d0' : '#fafaf7'} />

      {/* Venue interior, clipped to polygon shape */}
      <g clipPath={polygonStr ? `url(#${clipId})` : undefined}>
        <rect width={venueWidth} height={venueHeight} fill="#fafaf7" />
        <rect width={venueWidth} height={venueHeight} fill={`url(#${gridId})`} />
      </g>

      {/* Venue boundary outline */}
      {polygonStr ? (
        <polygon
          points={polygonStr}
          fill="none"
          stroke="#9e8b72"
          strokeWidth={2}
          strokeDasharray="8 4"
          style={{ pointerEvents: 'none' }}
        />
      ) : (
        <rect
          x={1}
          y={1}
          width={venueWidth - 2}
          height={venueHeight - 2}
          fill="none"
          stroke="#9e8b72"
          strokeWidth={2}
          strokeDasharray="8 4"
          rx={4}
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* Polygon line preview (connecting dots while editing) */}
      {editingPolygon && venuePolygon?.length >= 2 && (
        <polyline
          points={venuePolygon.map((p) => `${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke="#2a5d32"
          strokeWidth={1.5}
          strokeDasharray="5 3"
          opacity={0.5}
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* Vertex handles */}
      {editingPolygon &&
        venuePolygon?.map((pt, i) => (
          <PolygonHandle
            key={i}
            x={pt.x}
            y={pt.y}
            index={i}
            zoom={zoom}
            polygon={venuePolygon}
            onPolygonChange={onPolygonChange}
          />
        ))}

      {/* Hint text */}
      {editingPolygon && (
        <text
          x={venueWidth / 2}
          y={venueHeight - 10}
          textAnchor="middle"
          fontSize={11}
          fill="rgba(42,93,50,0.7)"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          Kattints pont hozzáadásához · Duplakatt = pont törlése (min. 3 pont)
        </text>
      )}
    </svg>
  );
}
